import React, { useEffect } from "react";
import { QuestionProps } from "../../survey";
import SelectQuestionWrapper from "../../../components/base_ui/higher_order_components/questions/select_question_wrapper";
import DeprecatedSelect from "./deprecated_select";
import {
  Answer,
  Customizations,
  isOptional,
  getOptionsForSelectComponent,
  getQuestionOptions,
} from "./select_shared_utils";
import { getOptionObjectFromText, getOptionOid, getOptionText } from "../question_helpers";

const SELECT_FOLLOW_UP_CHARACTER_LIMIT = 50;

export default function SurveyQuestionSelect({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
  dataTest = "survey-question-select",
}: QuestionProps<Answer, Customizations>) {
  const selectedOption = currentAnswer?.selectedOption;
  const optional = isOptional(customizations);

  // These options have the structure of { id: string, label: string }[] needed for the SelectQuestionWrapper component
  const selectComponentOptions = getOptionsForSelectComponent(
    customizations.options,
    customizations.withOtherOption,
  );

  // These options have the structure of { oid: string, text: string }[] needed to transform the component option selected
  // to a valid question answer.
  const questionOptions = getQuestionOptions(
    customizations.options,
    customizations.withOtherOption,
  );

  const followUpOptionSelected =
    customizations.followUp &&
    currentAnswer &&
    getOptionText(selectedOption) === customizations.followUp?.option;

  const otherOptionSelected = currentAnswer && getOptionOid(selectedOption) === "other";

  const showFollowUp = followUpOptionSelected || otherOptionSelected;

  const { followUpResponse, writeIn } = currentAnswer || {};
  const followUpCharacterCount = followUpResponse?.length ?? writeIn?.length ?? 0;
  const maximumCharacterLimitExceededBy = followUpCharacterCount - SELECT_FOLLOW_UP_CHARACTER_LIMIT;

  // The logic implemented here is mirrored from the deprecated select component
  const followUpIsOptional =
    typeof customizations.followUp?.optional === "undefined" ? true : customizations.optional;

  const followUpRequiredAndWithinCharacterLimit =
    !followUpIsOptional &&
    showFollowUp &&
    followUpCharacterCount > 0 &&
    maximumCharacterLimitExceededBy <= 0;

  const followUpNotRequiredAndWithinCharacterLimit =
    followUpIsOptional && showFollowUp && maximumCharacterLimitExceededBy <= 0;

  const noFollowUpIsAsked = !customizations.followUp && !otherOptionSelected;

  const followUpIsValid =
    followUpRequiredAndWithinCharacterLimit ||
    followUpNotRequiredAndWithinCharacterLimit ||
    noFollowUpIsAsked ||
    !showFollowUp;

  const answerIsValid = optional || !!currentAnswer;

  const followUpCurrentCharacterCount = followUpCharacterCount ?? 0;

  const followUpCharacterLimitExceeded = !followUpIsValid && maximumCharacterLimitExceededBy > 0;

  const requiredErrorMessage = "Please complete the required field.";
  const characterLimitErrorMessage = `Maximum character limit exceeded by ${maximumCharacterLimitExceededBy} character${maximumCharacterLimitExceededBy > 1 ? "s" : ""}.`;

  const followUpMissingButRequired =
    !followUpIsValid && showValidation && currentAnswer?.followUpResponse === undefined;

  const followUpAriaLabel =
    showFollowUp && !customizations.followUp?.prompt
      ? `Follow-up input field for: ${currentAnswer.label}`
      : otherOptionSelected
        ? `Follow-up input field for: ${customizations.withOtherOption?.text}`
        : undefined;

  const ableToSubmit = answerIsValid && followUpIsValid;

  useEffect(() => {
    setAbleToSubmit(ableToSubmit as boolean);
  }, [ableToSubmit, setAbleToSubmit]);

  const select = (
    <SelectQuestionWrapper
      label={customizations.prompt}
      secondaryText={customizations.description}
      optional={optional}
      options={selectComponentOptions}
      value={selectComponentOptions.find(
        (option) =>
          option.id === getOptionOid(selectedOption) ||
          option.label === getOptionText(selectedOption),
      )}
      onChange={(newAnswer) => {
        updateAnswer({
          selectedOption: newAnswer
            ? getOptionObjectFromText(newAnswer.label as string, questionOptions)
            : undefined,
        });
      }}
      followUpPrompt={otherOptionSelected ? "" : customizations.followUp?.prompt}
      followUpIsOptional={followUpIsOptional}
      showFollowUp={showFollowUp}
      followUpMaxCharacterCount={SELECT_FOLLOW_UP_CHARACTER_LIMIT}
      followUpErrorMessage={
        followUpCharacterLimitExceeded
          ? characterLimitErrorMessage
          : followUpMissingButRequired
            ? requiredErrorMessage
            : undefined
      }
      followUpCurrentCharacterCount={followUpCurrentCharacterCount}
      followUpAriaLabel={followUpAriaLabel}
      errorMessage={!answerIsValid && showValidation ? requiredErrorMessage : undefined}
      onChangeFollowUpValue={(e) => {
        updateAnswer({
          ...currentAnswer,
          [followUpOptionSelected ? "followUpResponse" : "writeIn"]: e.target.value,
        });
      }}
      dataTest={dataTest}
    />
  );

  return showRefreshedComponents ? (
    select
  ) : (
    <DeprecatedSelect
      customizations={customizations}
      updateAnswer={updateAnswer}
      currentAnswer={currentAnswer}
      setAbleToSubmit={setAbleToSubmit}
    />
  );
}
